import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUsers, FiCalendar, FiUser, FiSearch, FiMail, FiDownload } from 'react-icons/fi';
import { useParams, useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import Notification from '../common/Notification';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    margin-bottom: 1.5rem;
  }
`;


const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    justify-content: center;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;


const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ExportButton = styled.button`
  background: var(--primary-color);
  border: none;
  color: var(--text-inverse);
  cursor: pointer;
  padding: 0.75rem 1.5rem;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
  
  &:hover {
    background: var(--primary-hover);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const CourseInfo = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CourseName = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    text-align: center;
  }
`;

const CourseDetails = styled.div`
  display: flex;
  gap: 2rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: var(--shadow-md);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1rem 1rem 2.5rem;
    font-size: 1rem;
  }
`;

const StudentsTable = styled.div`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  overflow: hidden;
  
  @media (max-width: 768px) {
    border: none;
    background: transparent;
    border-radius: 0;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 2fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background-color: var(--background-secondary);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 2fr 2fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    border-radius: 0;
    margin-bottom: 0.75rem;
    border: 1px solid var(--border-color);
    background: var(--background-primary);
    box-shadow: var(--shadow-sm);
    
    /* Hide email, enrollment date, and status on mobile */
    > *:nth-child(3),
    > *:nth-child(4),
    > *:nth-child(5) {
      display: none;
    }
    
    /* Avatar styling */
    > *:first-child {
      flex-shrink: 0;
    }
    
    /* Student info container */
    > *:nth-child(2) {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0;
  background: var(--primary-color);
  color: var(--text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 1rem;
  }
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const StudentName = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    font-weight: 600;
  }
`;

const StudentEmail = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.8rem;
  }
`;

const EnrollmentDate = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.75rem;
  }
`;

const StudentStatus = styled.span`
  color: ${props => props.isActive ? 'var(--success-color)' : 'var(--error-color)'};
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: lowercase;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.75rem;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  &.active {
    background-color: rgba(56, 161, 105, 0.1);
    color: var(--success-color);
    border: 1px solid rgba(56, 161, 105, 0.2);
  }
  
  &.inactive {
    background-color: rgba(220, 38, 38, 0.1);
    color: var(--error-color);
    border: 1px solid rgba(220, 38, 38, 0.2);
  }
`;


const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  color: var(--text-muted);
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  color: var(--error-color);
  text-align: center;
`;

const EmptyContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  text-align: center;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-sm);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
  color: var(--text-muted);
`;

const EmptyTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
`;

const EmptyDescription = styled.p`
  font-size: 0.875rem;
  margin: 0;
  color: var(--text-secondary);
  opacity: 0.8;
`;

const EnrolledStudentsPage = () => {
  const { courseId, electiveId } = useParams();
  const location = useLocation();
  const isElective = location.pathname.includes('/electives/');
  const entityId = isElective ? electiveId : courseId;
  
  const [entity, setEntity] = useState(null);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // showBackToTop removed - using GlobalScrollToTop from App.js
  const [sendingEmail, setSendingEmail] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });

  const fetchEntityAndStudents = useCallback(async () => {
    if (!entityId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch entity (course or elective) details and enrolled students
      console.log(`Fetching ${isElective ? 'elective' : 'course'} data for ID:`, entityId);
      const response = isElective 
        ? await apiService.getElective(entityId)
        : await apiService.getCourse(entityId);
      console.log(`${isElective ? 'Elective' : 'Course'} response:`, response);
      
      if (response.success && response[isElective ? 'elective' : 'course']) {
        const entityData = response[isElective ? 'elective' : 'course'];
        setEntity(entityData);
        setEnrolledStudents(entityData.students || []);
      } else {
        throw new Error(`API returned: ${JSON.stringify(response)}`);
      }
    } catch (err) {
      console.error(`Error fetching ${isElective ? 'elective' : 'course'} and students:`, err);
      console.error('Entity ID:', entityId);
      console.error('Error details:', err.message);
      setError(`Failed to load ${isElective ? 'elective' : 'course'} and student data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [entityId, isElective]);

  useEffect(() => {
    fetchEntityAndStudents();
  }, [fetchEntityAndStudents]);

  // Scroll to top on mobile when component mounts
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Scroll handlers removed - using GlobalScrollToTop from App.js

  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return enrolledStudents;
    
    const term = searchTerm.toLowerCase();
    return enrolledStudents.filter(student => 
      student.first_name.toLowerCase().includes(term) ||
      student.last_name.toLowerCase().includes(term) ||
      student.email.toLowerCase().includes(term)
    );
  }, [enrolledStudents, searchTerm]);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const emailCSV = async () => {
    if (!entity || filteredStudents.length === 0) return;
    
    try {
      setSendingEmail(true);
      
      const headers = ['Name', 'Email', 'Enrollment Date', 'Status'];
      const rows = filteredStudents.map(student => [
        `${student.first_name} ${student.last_name}`,
        student.email,
        formatDate(student.enrolled_at),
        student.status || 'active'
      ]);
      
      const csvContent = [headers, ...rows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');
      
      const response = await apiService.sendCSVEmail({
        courseId: isElective ? null : entity.id,
        courseName: entity.name,
        courseCode: entity.code,
        csvContent: csvContent,
        studentCount: filteredStudents.length
      });
      
      if (response.success) {
        setNotification({
          isOpen: true,
          type: 'success',
          title: 'Email Sent',
          message: 'CSV report has been sent to your email successfully!'
        });
      } else {
        throw new Error(response.message || 'Failed to send CSV email');
      }
    } catch (error) {
      console.error('Error sending CSV email:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Email Failed',
        message: error.message || 'Failed to send CSV email. Please try again.'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadCSV = () => {
    if (!entity || filteredStudents.length === 0) return;
    
    const headers = ['Name', 'Email', 'Enrollment Date', 'Status'];
    const rows = filteredStudents.map(student => [
      `${student.first_name} ${student.last_name}`,
      student.email,
      formatDate(student.enrolled_at),
      student.status || 'active'
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${entity.code}_students_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <div>Loading enrolled students...</div>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <div>{error}</div>
        </ErrorContainer>
      </Container>
    );
  }

  if (!entity) {
    return (
      <Container>
        <ErrorContainer>
          <div>{isElective ? 'Elective' : 'Course'} not found</div>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FiUsers size={24} />
          Enrolled Students
        </Title>
      </Header>

      <CourseInfo
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <CourseName>{entity.name}</CourseName>
        <CourseDetails>
          <DetailItem>
            <FiUser size={14} />
            {isElective ? 'Elective' : 'Course'} Code: {entity.code}
          </DetailItem>
          <DetailItem>
            <FiUsers size={14} />
            {enrolledStudents.length} enrolled
          </DetailItem>
          <DetailItem>
            <FiCalendar size={14} />
            Max: {entity.max_students} students
          </DetailItem>
        </CourseDetails>
      </CourseInfo>

      {!loading && !error && enrolledStudents.length > 0 && (
        <>
          {filteredStudents.length > 0 && (
            <ButtonContainer>
              <ExportButton onClick={downloadCSV}>
                <FiDownload size={16} />
                Download Student Report
              </ExportButton>
              <ExportButton onClick={emailCSV} disabled={sendingEmail}>
                <FiMail size={16} />
                {sendingEmail ? 'Sending...' : 'Email Student Report'}
              </ExportButton>
            </ButtonContainer>
          )}
          
          <SearchContainer>
            <SearchIcon>
              <FiSearch size={16} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search students by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          <StudentsTable>
            <TableHeader>
              <div>Student</div>
              <div>Name</div>
              <div>Email</div>
              <div>Enrolled Date</div>
              <div>Status</div>
            </TableHeader>
            
            {filteredStudents.map((student, index) => (
              <TableRow
                key={student.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0 }}
              >
                <Avatar>
                  {getInitials(student.first_name, student.last_name)}
                </Avatar>
                
                <StudentInfo>
                  <StudentName>{student.first_name} {student.last_name}</StudentName>
                  <StudentEmail>{student.email}</StudentEmail>
                  <EnrollmentDate>{formatDate(student.enrolled_at)}</EnrollmentDate>
                  <StudentStatus isActive={student.status === 'active' || !student.status}>
                    {student.status || 'active'}
                  </StudentStatus>
                </StudentInfo>
                
                <div style={{ fontSize: '0.95rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                  {student.email}
                </div>
                
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {formatDate(student.enrolled_at)}
                </div>
                
                <StatusBadge className={student.status || 'active'}>
                  {student.status || 'active'}
                </StatusBadge>
              </TableRow>
            ))}
          </StudentsTable>
        </>
      )}

      {!loading && !error && enrolledStudents.length === 0 && (
        <EmptyContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <EmptyIcon>
            <FiUsers size={48} />
          </EmptyIcon>
          <EmptyTitle>No Students Enrolled</EmptyTitle>
          <EmptyDescription>
            This {isElective ? 'elective' : 'course'} doesn't have any enrolled students yet.
          </EmptyDescription>
        </EmptyContainer>
      )}

      {!loading && !error && enrolledStudents.length > 0 && filteredStudents.length === 0 && (
        <EmptyContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <EmptyIcon>
            <FiSearch size={48} />
          </EmptyIcon>
          <EmptyTitle>No Students Found</EmptyTitle>
          <EmptyDescription>
            No students match your search criteria.
          </EmptyDescription>
        </EmptyContainer>
      )}

      {/* Back to Top Button removed - using GlobalScrollToTop from App.js */}

      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </Container>
  );
};

export default EnrolledStudentsPage;
